/**
 * Display formatting. German conventions throughout (E-29): thousands dot,
 * decimal comma. Normalisation itself happens in the backend (`core/units.py`);
 * nothing here parses user input into production values.
 */

/**
 * Fractions are shown, not rounded away. A reading normalised to 2,88 mm is a
 * finding — printing it as "3 mm" would hide the very thing somebody has to
 * look at. Whole millimetres, which is nearly all of them, are unaffected.
 */
const MM = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 });
const METRES = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const MONEY = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const AREA = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 3, maximumFractionDigits: 4 });
const PERCENT = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const FACTOR = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 });
const KG = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
/** `5200` → `5.200 mm`. The unit is never dropped — a bare number is a defect. */
export function formatMm(value: number | null): string {
  if (value === null) return "—";
  return `${MM.format(value)} mm`;
}

/** `5200` → `5,20 m`, for the secondary reading next to the millimetre value. */
export function formatMetres(value: number | null): string {
  if (value === null) return "—";
  return `${METRES.format(value / 1000)} m`;
}

/**
 * `"1281.00"` → `"1.281,00 €"`.
 *
 * The backend sends a price as a decimal **string**, never a JSON number — a
 * cent lost to float arithmetic on the way to a quotation is a cent wrong on
 * it (see `ConsoleLineOut`, `apps/api/app/demo.py`). Parsed here at the last
 * possible moment, for display only: this browser never adds or re-totals a
 * price, the sums it shows are the backend's own.
 */
export function formatMoney(value: string | null): string {
  if (value === null) return "—";
  return `${MONEY.format(Number(value))} €`;
}

/**
 * `"0.288"` → `"0,288 m²"`. Three to four decimals: the wire carries the
 * area to four (`dto.py`'s `_fixed(line.area_m2, 4)`), and a band-gap area
 * like 0,2505 m² is a figure the engine specifically computed to that
 * precision — rounding it to 0,251 m² would round the very number the price
 * is computed from, so "50,00 €/m² × 0,251 m²" would no longer multiply out
 * to the EK shown next to it (fix round 2, item 11). Three stays the floor:
 * an even value like 0,300 m² does not sprout a trailing zero it does not
 * carry. The wire carries the area as a decimal string like a price.
 */
export function formatArea(value: string | null): string {
  if (value === null) return "—";
  return `${AREA.format(Number(value))} m²`;
}

/**
 * `"30"` → `"30,0 %"`. For margins and shares — business figures the server
 * computed and the person checks. **Not** for a confidence: the extraction
 * states a level and `ConfidenceMeter` shows that word (AN-14); the absence
 * of `formatConfidence` is asserted in `format.test.ts` and stays.
 */
export function formatPercent(value: string | null): string {
  if (value === null) return "—";
  return `${PERCENT.format(Number(value))} %`;
}

/** `"1.3"` → `"×1,3"` — a rule factor, written the way the price list writes it. */
export function formatFactor(value: string): string {
  return `×${FACTOR.format(Number(value))}`;
}

/** `"40.9"` → `"40,9 kg"` — the freight weight, to a tenth as the engine rounds it. */
export function formatKg(value: string | null): string {
  if (value === null) return "—";
  return `${KG.format(Number(value))} kg`;
}

/**
 * `"2026-01-19"` → `"19.01.2026"`. Built from the three parts rather than via
 * `new Date(iso)`: a date-only ISO string parses as UTC midnight, and in a
 * western time zone that is the evening before. A price list's validity date
 * has no time of day to be shifted by.
 */
export function formatDate(isoDate: string): string {
  const [year, month, day] = isoDate.slice(0, 10).split("-").map(Number);
  if (year === undefined || month === undefined || day === undefined) return isoDate;
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return isoDate;
  return new Intl.DateTimeFormat("de-DE", { dateStyle: "medium" }).format(
    new Date(year, month - 1, day),
  );
}

// There is deliberately no `formatConfidence`. The extraction states a level —
// `high`, `medium`, `low` — and `ConfidenceMeter` shows that word. A helper
// that turns a confidence into "93 %" existed here and was used exactly once,
// and it put a precision on the screen that nobody measured (AN-14). It is
// removed rather than left unused: an available percent formatter is an
// invitation.

/** Whole kilobytes, one place for megabytes, no trailing zero („8 kB", „1,2 MB", „2 MB"). */
const KILOBYTES = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 });
const MEGABYTES = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 });

/**
 * A file's size as a person reads it (record fix wave): „8,00 kB" claimed a
 * precision to the hundredth nobody uses.
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${KILOBYTES.format(bytes / 1024)} kB`;
  return `${MEGABYTES.format(bytes / (1024 * 1024))} MB`;
}

export function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(iso));
}

/**
 * `"2026-09-21T13:40:00Z"` → `"21.09.2026"`, in the browser's time zone.
 * For **timestamps** — when a calculation ran, when a handover expires.
 * `formatDate` above is for calendar dates without a time (a price list's
 * validity) and deliberately does not go through `Date`.
 */
export function formatDay(iso: string): string {
  return new Intl.DateTimeFormat("de-DE", { dateStyle: "medium" }).format(new Date(iso));
}

/**
 * Two letters, no full stop — `Intl` says „Mo." in de-DE, and the mockup's
 * „Mo 16:02" is the register's own device. A table, so no locale data can
 * change a column that is meant to line up.
 */
const WEEKDAY = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"] as const;

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/** Whole calendar days from `at` back to `now`, clamped at 0 (clock skew is not the future). */
function daysAgo(at: Date, now: Date): number {
  return Math.max(0, Math.round((startOfDay(now).getTime() - startOfDay(at).getTime()) / 86_400_000));
}

/** `"10:14"` — the time of a timestamp, local, for the timeline's time column. */
export function clockTime(iso: string): string {
  const at = new Date(iso);
  return `${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

/** `"2026-09-28"` — the local calendar day of a timestamp; the timeline groups by it. */
export function dayKey(iso: string): string {
  const at = new Date(iso);
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

export interface ActivityMoment {
  /** „heute" / „gestern" / a two-letter weekday — `null` once the digits alone are enough (older than a week). */
  word: string | null;
  /** The digits: a clock time within the week, a day.month. (plus year) past it. */
  digits: string;
}

/**
 * `activityTime` split into its word and its digits (register fix round 1,
 * M7): the word is prose and wants the UI font, the digits are a column of
 * figures and want `.num` — a whole reading in `.num` sets „heute"/„gestern"/
 * „Mo" in tabular-figure monospace, which is a typeface for numbers wearing
 * a word it was never cut for.
 */
export function activityMoment(iso: string, now: Date = new Date()): ActivityMoment {
  const at = new Date(iso);
  const days = daysAgo(at, now);
  const time = clockTime(iso);
  if (days === 0) return { word: "heute", digits: time };
  if (days === 1) return { word: "gestern", digits: time };
  // `getDay()` is always 0-6, so the lookup is never actually out of bounds.
  if (days <= 6) return { word: WEEKDAY[at.getDay()]!, digits: time };
  const dayMonth = `${pad(at.getDate())}.${pad(at.getMonth() + 1)}.`;
  return { word: null, digits: at.getFullYear() === now.getFullYear() ? dayMonth : `${dayMonth}${at.getFullYear()}` };
}

/**
 * „Zuletzt" in the register (UX ruling 4, F-2): as precise as the mockup —
 * „heute 10:14", „gestern 17:22", „Mo 16:02" within the week, „12.09." in
 * this year, „12.09.2025" before. `relativeDay` stays for the start page,
 * where a day is enough.
 */
export function activityTime(iso: string, now: Date = new Date()): string {
  const { word, digits } = activityMoment(iso, now);
  return word !== null ? `${word} ${digits}` : digits;
}

/** The timeline's day head (UX ruling 6, F-6): „Heute · Mo 28.09.", „Gestern · So 27.09.", „Mi 23.09.2026". */
export function dayHeading(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  const days = daysAgo(at, now);
  const short = `${WEEKDAY[at.getDay()]} ${pad(at.getDate())}.${pad(at.getMonth() + 1)}.`;
  if (days === 0) return `Heute · ${short}`;
  if (days === 1) return `Gestern · ${short}`;
  return `${short}${at.getFullYear()}`;
}

/**
 * The day `days` days from `from`, as `formatDay` prints it — the handover
 * dialog's „verfällt am 05.10.2026" (UX ruling 10). A function rather than
 * `Date.now()` in a component body, which the React lint reads as impure.
 */
export function dayInDays(days: number, from: Date = new Date()): string {
  const later = new Date(from);
  later.setDate(later.getDate() + days);
  return formatDay(later.toISOString());
}

/**
 * The value an `<input type="datetime-local">` takes: local time, minutes, no
 * zone. Without an argument, now — so a component can seed a field with it
 * without calling `new Date()` in its body (the React lint's purity rule).
 */
export function localDateTimeValue(date: Date = new Date()): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * ISO-8601 week number (Monday-based; week 1 is the week holding the year's
 * first Thursday). Exported on its own so the year boundary — a week-52/53
 * date rolling into next year's week 1, or vice versa — can be pinned
 * directly against a fixed `Date`, without faking "today" for `relativeDay`.
 */
export function isoWeek(date: Date): number {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNumber = (target.getUTCDay() + 6) % 7; // Monday = 0 … Sunday = 6
  target.setUTCDate(target.getUTCDate() - dayNumber + 3); // nearest Thursday
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const firstDayNumber = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNumber + 3);
  return 1 + Math.round((target.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * `statusChangedAt` (design §5.3), read the way a person reads a calendar
 * rather than a machine reads a timestamp: "heute" / "gestern" carry no
 * date at all, a short weekday name still orients within the working week,
 * and past that a person is back to "which week was that" — `KW ${n}`. The
 * row already carries the stage; a full "07.09.2026 14:32" would answer a
 * question nobody asked at a glance.
 */
export function relativeDay(iso: string): string {
  const target = new Date(iso);
  // Clamped at 0: a timestamp in the future is clock skew between the server
  // and this browser, not a date. Without the clamp a stamp a few minutes
  // ahead falls through to `KW ${n}` — the current week, printed as though it
  // were long past.
  const diffDays = Math.max(
    0,
    Math.round((startOfDay(new Date()).getTime() - startOfDay(target).getTime()) / 86_400_000),
  );
  if (diffDays === 0) return "heute";
  if (diffDays === 1) return "gestern";
  if (diffDays >= 2 && diffDays <= 6) {
    return target.toLocaleDateString("de-DE", { weekday: "short" });
  }
  return `KW ${isoWeek(target)}`;
}
